import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, Download, Zap, CheckCircle2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const SHOW_DELAY_MS = 25_000;
const LS_DISMISSED = "fs_lead_popup_dismissed_at";
const LS_CAPTURED = "fs_lead_popup_captured";
const COOLDOWN_DAYS = 7; // After dismissal, don't re-show for 7 days

// Paths where the popup is allowed
const ALLOWED_PREFIXES = ["/", "/services", "/about", "/blog", "/meetups", "/equipment", "/library", "/in-person-training", "/social", "/testimonials", "/faq", "/contact"];
function isAllowedPath(pathname) {
  if (!pathname) return false;
  // Block anything in /admin, /portal, /login, /register, /book, /payment-*, /terms, /waiver
  const blocked = ["/admin", "/portal", "/login", "/register", "/book", "/payment-", "/terms", "/waiver"];
  if (blocked.some((p) => pathname.startsWith(p))) return false;
  // Otherwise allow if we match one of the marketing paths (exact or prefix with /)
  return ALLOWED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p === "/" ? "/" : `${p}/`) || pathname === p);
}

export function LeadMagnetPopup() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [magnet, setMagnet] = useState(null); // {id,slug,title,description,cover_image}
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", goal: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // download_url after success

  // Single allowed check
  const allowed = isAllowedPath(location.pathname);

  // Fetch featured magnet (once)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/newsletter/featured-magnet");
        if (!cancelled) setMagnet(data?.resource || null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Check if user already captured or recently dismissed
  const isSuppressed = useCallback(() => {
    try {
      if (localStorage.getItem(LS_CAPTURED)) return true;
      const dismissedAt = localStorage.getItem(LS_DISMISSED);
      if (dismissedAt) {
        const days = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
        if (days < COOLDOWN_DAYS) return true;
      }
    } catch { /* ignore */ }
    return false;
  }, []);

  const triggerOpen = useCallback(() => {
    if (!allowed) return;
    if (isSuppressed()) return;
    setOpen(true);
  }, [allowed, isSuppressed]);

  // Timer trigger — 25s after route-mount (resets per allowed page visit)
  useEffect(() => {
    if (!allowed || isSuppressed()) return;
    const t = setTimeout(triggerOpen, SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [allowed, isSuppressed, triggerOpen, location.pathname]);

  // Exit-intent trigger — fires when mouse leaves the viewport via the top edge
  useEffect(() => {
    if (!allowed || isSuppressed()) return;
    const onMouseOut = (e) => {
      // Only fire if pointer left through the top of the page
      if (!e.relatedTarget && e.clientY <= 0) triggerOpen();
    };
    document.addEventListener("mouseout", onMouseOut);
    return () => document.removeEventListener("mouseout", onMouseOut);
  }, [allowed, isSuppressed, triggerOpen]);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem(LS_DISMISSED, String(Date.now())); } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.full_name.trim() || !form.email.trim()) {
      toast.error("Name and email required");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        ...form, source: "popup",
      });
      setDone(data?.download_url || null);
      try { localStorage.setItem(LS_CAPTURED, "1"); } catch { /* ignore */ }
      toast.success(magnet ? "Check your email for the download." : "You're in — check your inbox.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !allowed) return null;

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const downloadHref = done ? `${apiBase}${done}` : null;
  const coverSrc = magnet?.cover_image || null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      data-testid="lead-popup-overlay"
      onClick={close}
    >
      <div
        className="relative w-full sm:max-w-3xl bg-[#0A0A0A] border border-[#27272A] border-t-4 border-t-[#FF331F] rounded-t-md sm:rounded-md shadow-2xl overflow-hidden grid grid-cols-1 sm:grid-cols-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="lead-popup"
      >
        <button
          type="button" onClick={close}
          className="absolute top-3 right-3 z-10 p-1.5 bg-black/60 hover:bg-[#FF331F] text-white rounded-sm transition-colors"
          data-testid="lead-popup-close"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Cover */}
        <div className="sm:col-span-2 bg-[#050505] relative min-h-[140px] sm:min-h-full">
          {coverSrc ? (
            <img src={coverSrc} alt={magnet?.title || "Lead magnet"} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-6">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FF331F]/15 border border-[#FF331F]/30 rounded-sm mb-3">
                  <Zap className="h-7 w-7 text-[#FF331F]" />
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Free Insider Drops</div>
              </div>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black via-black/60 to-transparent sm:hidden" />
        </div>

        {/* Form */}
        <div className="sm:col-span-3 p-6 sm:p-8">
          {!done ? (
            <>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#FF331F] font-bold mb-2">{magnet ? "Free Download" : "Insider Newsletter"}</p>
              <h2 className="font-display text-2xl sm:text-3xl text-white uppercase tracking-tight leading-tight mb-3" data-testid="lead-popup-title">
                {magnet?.title || "Box smarter — straight from Coach Chris."}
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                {magnet?.description || "Get the boxing fundamentals cheatsheet plus a 14-day email series breaking down how Coach actually teaches the basics. No spam, unsubscribe anytime."}
              </p>

              <form onSubmit={submit} className="space-y-3">
                <Input
                  required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Your name" autoComplete="name"
                  className="bg-[#050505] border-[#27272A] text-white rounded-sm h-11"
                  data-testid="lead-popup-name"
                />
                <Input
                  required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email address" autoComplete="email"
                  className="bg-[#050505] border-[#27272A] text-white rounded-sm h-11"
                  data-testid="lead-popup-email"
                />
                <Input
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone (optional)" autoComplete="tel"
                  className="bg-[#050505] border-[#27272A] text-white rounded-sm h-11"
                  data-testid="lead-popup-phone"
                />
                <Textarea
                  value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })}
                  placeholder="What's your #1 boxing goal? (optional)"
                  className="bg-[#050505] border-[#27272A] text-white rounded-sm min-h-[60px]"
                  data-testid="lead-popup-goal"
                />
                <Button
                  type="submit" disabled={submitting}
                  className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs rounded-sm h-12"
                  data-testid="lead-popup-submit"
                >
                  {submitting ? "Sending…" : (magnet ? "Send Me The Download" : "Start The Series")}
                </Button>
                <p className="text-[10px] text-zinc-600 text-center">By signing up, you agree to receive emails. Unsubscribe anytime.</p>
              </form>
            </>
          ) : (
            <div className="text-center py-8" data-testid="lead-popup-success">
              <CheckCircle2 className="h-14 w-14 text-[#FF331F] mx-auto mb-4" />
              <h3 className="font-display text-2xl text-white uppercase tracking-tight mb-2">You're in.</h3>
              <p className="text-sm text-zinc-400 mb-6">
                {magnet ? "Your download is below. We also emailed a copy — and Day 1 of the 14-day series will hit your inbox in a couple of minutes." : "First email is on its way."}
              </p>
              {downloadHref && (
                <a
                  href={downloadHref} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 rounded-sm"
                  data-testid="lead-popup-download"
                >
                  <Download className="h-4 w-4" /> Download Now
                </a>
              )}
              <div className="mt-6">
                <button onClick={close} className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-white" data-testid="lead-popup-keep-browsing">
                  Keep browsing →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
